import React from 'react';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../utils/intl';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import { useGoBack } from '../../../../../utils/appNavigationNames';
import useMedicalProfileCardsCards from '../../../../../hooks/api/useMedicalProfileCards';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import LargeDescriptiveImage from '../../../../v2BaseComponents/LargeDescriptiveImage';
import urls from '../../../../../utils/urls';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import FullScreenImageView from '../../../../v2BaseComponents/FullScreenImageView/FullScreenImageView';
import { AvatarAPIImagesProvider } from '../../../../v2BaseComponents/FullScreenImageView';

const intl = getIntl('card-view', {
    headerTemplate: 'View {cardType}',
    description: '{cardType} Description',
    image: '{cardType} Image',
    id: 'ID Card',
    insurance: 'Insurance Card',
    delete: 'Delete {cardType}',
    cancelEdit: 'Cancel Edit',
    edit: 'Edit',
});

export type ViewCardOwnProps = {
    onEdit: () => void;
    profileId: string;
    cardType: 'id' | 'insurance';
    cardId: string;
    description: string;
};

export type ViewCardProps = ViewCardOwnProps;

const ViewCard: React.FC<ViewCardProps> = ({ onEdit, profileId, cardType, cardId }) => {
    const imageUrl =
        cardType === 'id'
            ? urls.api.medicalProfiles.cards.idCardImage(profileId, cardId)
            : urls.api.medicalProfiles.cards.insuranceCardImage(profileId, cardId);
    const hasEdit = useHasPermission(profileId, 'idsAndInfo', 'edit');
    const goBack = useGoBack();
    const { isDeletingIdCard, deleteIdCard, deleteInsuranceCard, isDeletingInsuranceCard } =
        useMedicalProfileCardsCards({
            medicalProfileId: profileId,
            onDeleteCardSuccess: () => {
                goBack();
            },
        });
    return (
        <AnimateLoadedContent isLoading={isDeletingIdCard || isDeletingInsuranceCard}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    actions={[
                        {
                            icon: 'Edit2',
                            title: intl('edit'),
                            onPress: onEdit,
                            show: hasEdit,
                            testID: 'edit-card',
                        },
                        {
                            icon: 'Trash2',
                            title: intl('delete', { cardType: intl(cardType) }),
                            onPress: () => {
                                if (cardType === 'id') {
                                    deleteIdCard(cardId);
                                } else {
                                    deleteInsuranceCard(cardId);
                                }
                            },
                            show: hasEdit,
                            testID: 'delete-card',
                        },
                    ]}
                >
                    <MainContentContainer>
                        <AvatarAPIImagesProvider
                            images={[{ imageViewImageUrl: imageUrl, imageViewImageDescription: '' }]}
                        >
                            {(renderImagesData) => (
                                <FullScreenImageView
                                    renderImagesData={renderImagesData}
                                    renderImages={(images, onPress, getIsPressEnabled) => (
                                        <LargeDescriptiveImage
                                            imageUrl={imageUrl}
                                            description={intl('image', { cardType: intl(cardType) })}
                                            onPress={
                                                getIsPressEnabled(images[0]) ? () => onPress(images[0]) : undefined
                                            }
                                            pressableTestId="card-image"
                                        />
                                    )}
                                />
                            )}
                        </AvatarAPIImagesProvider>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ViewCard);
